import { useEffect, useRef, useState } from 'react';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// Scopes: identity (openid/email/profile) and read-only access to Google
// Calendar so the kiosk can show events from the shared family calendar.
// Adding calendar.readonly here forces Google to re-prompt both household
// members for consent on next sign-in, at which point Google returns a
// refresh token (because we're using the auth-code flow with offline
// access). The server exchanges that code at /api/auth/session and
// persists the encrypted refresh token to the users row.
const SIGN_IN_SCOPE =
  'openid email profile https://www.googleapis.com/auth/calendar.readonly';

interface CodeResponse {
  code?: string;
  scope?: string;
  error?: string;
}

interface CodeClient {
  requestCode: () => void;
}

interface GoogleAccountsOauth2 {
  initCodeClient: (config: {
    client_id: string;
    scope: string;
    ux_mode: 'popup' | 'redirect';
    /** Required to receive a refresh token. Without this, Google only
     *  returns an access token even with offline access requested. */
    select_account?: boolean;
    callback: (resp: CodeResponse) => void;
    error_callback?: (err: { type?: string; message?: string }) => void;
  }) => CodeClient;
}

declare global {
  interface Window {
    google?: { accounts: { oauth2: GoogleAccountsOauth2 } };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadGis(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GIS load failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('GIS load failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface GoogleButtonProps {
  clientId: string;
  onAuthCode: (code: string) => void;
  onError?: (err: Error) => void;
}

// Custom-branded sign-in button. Uses the OAuth 2.0 authorization-code
// flow (popup mode) rather than the implicit token flow, because we need
// a refresh token to keep reading the family calendar after the
// initial access token expires. The server exchanges the returned code
// at /api/auth/session for { id_token, access_token, refresh_token },
// then verifies identity, persists the encrypted refresh token to the
// users row, and returns a long-lived session token.
export function GoogleButton({ clientId, onAuthCode, onError }: GoogleButtonProps) {
  const clientRef = useRef<CodeClient | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !window.google?.accounts?.oauth2) return;
        clientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: SIGN_IN_SCOPE,
          ux_mode: 'popup',
          // Always show the account chooser so the household members
          // don't get silently signed in with the wrong account on a
          // shared kiosk session.
          select_account: true,
          callback: (resp) => {
            if (resp.error) {
              onError?.(new Error(resp.error));
              return;
            }
            if (resp.code) onAuthCode(resp.code);
          },
          error_callback: (err) => {
            // Google fires this for popup blocked, user-cancelled, etc.
            // Cancellation is the common case; suppress it to avoid scaring
            // the user with an error banner when they just closed the popup.
            if (err.type === 'popup_closed' || err.type === 'popup_failed_to_open') return;
            onError?.(new Error(err.message ?? err.type ?? 'Google sign-in failed'));
          },
        });
        setReady(true);
      })
      .catch((err: Error) => {
        if (!cancelled) onError?.(err);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, onAuthCode, onError]);

  return (
    <button
      type="button"
      disabled={!ready}
      onClick={() => clientRef.current?.requestCode()}
      className="btn btn-primary w-full"
    >
      Enter now
    </button>
  );
}

export function disableGoogleAutoSelect(): void {
  // OAuth 2.0 code flow has no "auto select" cookie to clear; sign-out is
  // a session-level concern handled by lock(). Kept as an exported function
  // for API compatibility with the previous GIS ID-token flow.
}
